import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import FirebaseContext from '../components/Firebase/context';
import { AuthUserContext } from '../components/Session';

/**
 * Real-time notifications hook.
 *
 * Keeps Firebase listeners alive for the full component lifetime so the bell
 * badge updates without requiring the user to open the panel first.
 *
 * Flow:
 *  1. Load the stored lastCheckTime from Firebase (previous session watermark).
 *  2. Items arriving AFTER the first "value" callback fire are genuine real-time pushes
 *     → those produce toasts (only when the panel is closed).
 *  3. markRead() is called when the user opens the panel: advances the local
 *     lastCheckTime, writes it to Firebase, and clears any queued toasts.
 */
export function useNotifications() {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);

  // null = still loading from Firebase (don't flash a false unread count)
  const [lastCheckTime, setLastCheckTime] = useState(null);
  // Latest message/log timestamps — used only for badge counting.
  // Full lists are fetched on-demand by the Notifications panel (once reads).
  const [latestMessageTimestamp, setLatestMessageTimestamp] = useState(0);
  const [latestLogPerOrg, setLatestLogPerOrg] = useState({});
  const [toasts, setToasts] = useState([]);

  // Notification preferences — persisted in localStorage, both disabled by default.
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return localStorage.getItem('notificationSoundEnabled') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [toastsEnabled, setToastsEnabled] = useState(() => {
    try {
      return localStorage.getItem('notificationToastsEnabled') === 'true';
    } catch (e) {
      return false;
    }
  });

  // Ref mirror so the real-time listeners can read the current value without
  // having to re-subscribe when the preference changes.
  const toastsEnabledRef = useRef(toastsEnabled);
  useEffect(() => {
    toastsEnabledRef.current = toastsEnabled;
  }, [toastsEnabled]);

  const soundEnabledRef = useRef(soundEnabled);
  const audioCtxRef = useRef(null);

  const playNotificationSound = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) { /* audio not supported */ }
  }, []);

  const orgsKey = useMemo(() => {
    if (!authUser?.roles) return '';
    return Object.keys(authUser.roles)
      .filter(k => authUser.roles[k] !== null)
      .sort()
      .join(',');
  }, [authUser?.roles]);

  // ── Load lastCheckTime from Firebase ──────────────────────────────────────
  useEffect(() => {
    if (!authUser || !firebase) return;
    let cancelled = false;

    const timer = setTimeout(() => {
      firebase.doGetNotificationCheckTime(authUser.uid)
        .then(savedTime => {
          if (cancelled) return;
          setLastCheckTime(savedTime || 0);
        })
        .catch(err => {
          if (!cancelled) setLastCheckTime(0);
        });
    }, 1500);

    return () => { 
      cancelled = true; 
      clearTimeout(timer);
    };
  }, [authUser?.uid, firebase]); 

  // ── Messages real-time listener (limitToLast=1) ─────────────────────────
  // Purpose: badge counting + toast on new arrival only.
  // Full list is fetched on-demand by the Notifications panel (once read).
  // Cost: ~400 bytes per message written (vs ~2 KB with limitToLast(5)).
  useEffect(() => {
    if (!firebase) return;

    let isFirstFire = true;
    const knownIds = new Set();
    let ref;

    const timer = setTimeout(() => {
      ref = firebase.messages().orderByChild('createdAt').limitToLast(1);
      ref.on('value', snapshot => {
        const obj = snapshot.val();
        if (!obj) return;

        // limitToLast returns ascending; with limit=1 there is exactly one key
        const key = Object.keys(obj)[0];
        const latest = { ...obj[key], uid: key };

        if (isFirstFire) {
          isFirstFire = false;
          knownIds.add(latest.uid);
          if (latest.createdAt) setLatestMessageTimestamp(latest.createdAt);
          return;
        }

        if (knownIds.has(latest.uid)) return;
        knownIds.add(latest.uid);

        const isVisibleToUser =
          !latest.organisation ||
          (authUser?.roles?.[latest.organisation] != null);
        if (isVisibleToUser) {
          setLatestMessageTimestamp(latest.createdAt || Date.now());
          if (soundEnabledRef.current) playNotificationSound();
          if (!toastsEnabledRef.current) return;
          setToasts(prev => [
            ...prev.slice(-2),
            {
              id: `msg-${latest.uid}-${Date.now()}`,
              type: 'message',
              icon: latest.userPhoto ? null : '💬',
              photoUrl: latest.userPhoto || null,
              title: latest.username || 'New message',
              body: latest.text || null,
              org: latest.organisation || null,
            },
          ]);
        }
      });
    }, 1500);

    return () => {
      clearTimeout(timer);
      if (ref) ref.off();
    };
  }, [firebase, authUser?.uid]); 

  // ── Activity-log real-time listeners (limitToLast=1 per org) ─────────────
  // Purpose: badge counting + toast on new arrival only.
  // Full history is fetched on-demand by the Notifications panel (once read).
  // Cost: ~600 bytes per org per write (vs ~30 KB with limitToLast(100)).
  useEffect(() => {
    if (!authUser?.roles || !firebase) return;

    const orgs = Object.keys(authUser.roles).filter(k => authUser.roles[k] !== null);
    if (orgs.length === 0) return;

    const firstFireFlags = {};
    const knownLogIds = new Set();
    
    // Track timers so we can clear them on unmount
    const timers = [];

    orgs.forEach(org => {
      firstFireFlags[org] = true;

      const timer = setTimeout(() => {
        firebase.onOrgActivityFeed(org, logs => {
          // logs is sorted newest-first; with limit=1 only one entry arrives
          const latest = logs[0];

          if (firstFireFlags[org]) {
            // Initial backfill — record ID and timestamp, no toast
            firstFireFlags[org] = false;
            if (latest?.id) knownLogIds.add(latest.id);
            if (latest?.timestamp) {
              setLatestLogPerOrg(prev => ({ ...prev, [org]: latest.timestamp }));
            }
          } else {
            // Real-time push — new entry arrived
            if (latest?.id && !knownLogIds.has(latest.id)) {
              knownLogIds.add(latest.id);
              setLatestLogPerOrg(prev => ({ ...prev, [org]: latest.timestamp || Date.now() }));
              if (soundEnabledRef.current) playNotificationSound();
              if (!toastsEnabledRef.current) return;
              setToasts(prev => [
                ...prev.slice(-2),
                {
                  id: `log-${latest.id}-${Date.now()}`,
                  type: 'log',
                  icon: getLogIcon(latest.action),
                  photoUrl: null,
                  title: formatLogAction(latest.action),
                  body: buildLogBody({ ...latest, organization: org }),
                  org,
                },
              ]);
            }
          }
        }, 1);
      }, 1500);
      
      timers.push(timer);
    });

    return () => {
      timers.forEach(t => clearTimeout(t));
      orgs.forEach(org => {
        try { firebase.offOrgActivityFeed(org, 1); } catch (e) { /* ignore */ }
      });
    };
  }, [authUser?.uid, firebase, orgsKey]);
  // ── markRead: call when user opens the panel ─────────────────────────────
  const markRead = useCallback(() => {
    if (!firebase || !authUser) return;
    const now = Date.now();
    setLastCheckTime(now);
    setToasts([]); // Clear any queued toasts — user is looking at the panel
    firebase.doSetNotificationCheckTime(authUser.uid).catch(() => {});
  }, [authUser?.uid, firebase]);

  const dismissToast = useCallback(id => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const pushToast = useCallback((toast) => {
    if (!toastsEnabledRef.current) return;
    if (soundEnabledRef.current) playNotificationSound();
    setToasts(prev => [
      ...prev.slice(-2),
      { id: `api-${Date.now()}-${Math.random().toString(36).slice(2)}`, ...toast },
    ]);
  }, []);

  const toggleSound = useCallback((enabled) => {
    soundEnabledRef.current = enabled;
    setSoundEnabled(enabled);
    try {
      localStorage.setItem('notificationSoundEnabled', enabled ? 'true' : 'false');
    } catch (e) { /* storage unavailable */ }
    if (enabled) playNotificationSound();
  }, [playNotificationSound]);

  const toggleToasts = useCallback((enabled) => {
    toastsEnabledRef.current = enabled;
    setToastsEnabled(enabled);
    try {
      localStorage.setItem('notificationToastsEnabled', enabled ? 'true' : 'false');
    } catch (e) { /* storage unavailable */ }
    if (!enabled) setToasts([]);
  }, []);

  const lct = lastCheckTime ?? 0;
  // True if the latest known message arrived after the last check time
  const unreadMessages = lastCheckTime !== null && latestMessageTimestamp > lct ? 1 : 0;
  // Count orgs that have a log entry newer than the last check time
  const unreadLogs = lastCheckTime !== null
    ? Object.values(latestLogPerOrg).filter(ts => ts > lct).length
    : 0;
  const unreadCount = unreadMessages + unreadLogs;

  return {
    lastCheckTime: lct,
    unreadCount,
    unreadMessages,
    unreadLogs,
    toasts,
    dismissToast,
    markRead,
    pushToast,
    soundEnabled,
    setSoundEnabled: toggleSound,
    toastsEnabled,
    setToastsEnabled: toggleToasts,
  };
}

// ── Helpers shared with NotificationActivityLogItem ──────────────────────────

function getLogIcon(action) {
  const map = {
    USER_SIGNED_IN: '🔑',
    USER_SIGNED_IN_GOOGLE: '🔑',
    USER_SIGNED_IN_MICROSOFT: '🔑',
    USER_SIGNED_OUT: '👋',
    PROJECT_CREATED: '📁',
    PROJECT_RENAMED: '✏️',
    PROJECT_UPDATED: '📝',
    PROJECT_DELETED: '🗑️',
    PAGE_ANNOTATED: '✍️',
    PAGE_SAVED: '💾',
    PLANNING_SAVED: '📅',
    ASSEMBLY_CREATED: '🔧',
    ASSEMBLY_DELETED: '❌',
    RESOURCE_SAVED: '📦',
    RESOURCE_UPDATED: '📦',
    MESSAGE_SENT: '💬',
    MESSAGE_EDITED: '✏️',
    MESSAGE_DELETED: '🗑️',
    DOCUMENT_UPLOADED: '📄',
    DOCUMENT_DELETED: '🗑️',
    WAREHOUSE_CREATED: '🏭',
    WAREHOUSE_UPDATED: '🏭',
    WAREHOUSE_DELETED: '🏭',
    STORAGE_LOCATION_CREATED: '📍',
    STORAGE_LOCATION_UPDATED: '📍',
    SUPPLIER_CREATED: '🏢',
    SUPPLIER_UPDATED: '🏢',
    SUPPLIER_DELETED: '🏢',
    PRODUCT_CREATED: '📦',
    PRODUCT_UPDATED: '📦',
    PRODUCT_DELETED: '📦',
    PURCHASE_ORDER_CREATED: '🛒',
    PURCHASE_ORDER_UPDATED: '🛒',
    PURCHASE_ORDER_DELETED: '🛒',
    SALES_ORDER_CREATED: '💰',
    SALES_ORDER_UPDATED: '💰',
    SALES_ORDER_DELETED: '💰',
    SHIPMENT_CREATED: '🚚',
    SHIPMENT_UPDATED: '🚚',
    SHIPMENT_DELETED: '🚚',
    RECEIPT_CREATED: '📥',
    RECEIPT_UPDATED: '📥',
    RECEIPT_DELETED: '📥',
    STOCK_TRANSFER_CREATED: '🔄',
    STOCK_TRANSFER_UPDATED: '🔄',
    STOCK_TRANSFER_DELETED: '🔄',
    STOCK_RESERVATION_CREATED: '🔒',
    STOCK_RESERVATION_UPDATED: '🔒',
    STOCK_RESERVATION_DELETED: '🔒',
    RETURN_ORDER_CREATED: '↩️',
    RETURN_ORDER_UPDATED: '↩️',
    RETURN_ORDER_DELETED: '↩️',
    CYCLE_COUNT_CREATED: '🔢',
    CYCLE_COUNT_UPDATED: '🔢',
    CYCLE_COUNT_DELETED: '🔢',
    CUSTOMER_CREATED: '👥',
    CUSTOMER_UPDATED: '👥',
    CUSTOMER_DELETED: '👥',
  };
  return map[action] || '📋';
}

function formatLogAction(action) {
  return action
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, l => l.toUpperCase());
}

function buildLogBody(log) {
  const parts = [];
  if (log.details?.projectName) parts.push(log.details.projectName);
  if (log.actorEmail) parts.push(log.actorEmail);
  return parts.join(' • ') || null;
}
